import { __assign, __read, __rest } from "tslib";
import React, { forwardRef, useRef, useCallback, useLayoutEffect, } from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import { throttle } from 'lodash-es';
import { ScrollBehaviour, ScrollDirection } from '@marvelapp/core';
import ConnectedHotspot from './Hotspot';
import { getScale, getScreenById, getBodyHotspots, getCurrentScale, getPreviousScreen, } from '../selectors';
import styles from './ScrollContainer.module.css';
import { ScrollSnapMarkers, getScrollSnapType } from './ScrollSnapMarkers';
import { scroll } from '../actionCreators';
import { SCROLL_CONTAINER_LAYER } from '../constants/userTest';
import { isRecording as isRecordingSelector, isPlayingUserTest as isPlayingSelector, } from '../selectors/replay';
import { getScrollPosition } from '../selectors/scrollContainer';
import { ScrollbarContainer, Scrollbars, useScrollbars } from './Scrollbars';
// A container(div) that can be scrolled by dragging.
//
// This gives desktop users without a trackpad a way of scrolling horizontally.
// In future we want to let users add controls like left/right buttons for more
// realistic prototypes.
var DesktopScrollContainer = forwardRef(function (_a, r) {
    var scrollBehaviour = _a.scrollBehaviour, children = _a.children, containerWidth = _a.containerWidth, containerHeight = _a.containerHeight, contentWidth = _a.contentWidth, contentHeight = _a.contentHeight, recordScroll = _a.onScroll, hasScrollbars = _a.hasScrollbars, scrollDirection = _a.scrollDirection, style = _a.style, props = __rest(_a, ["scrollBehaviour", "children", "containerWidth", "containerHeight", "contentWidth", "contentHeight", "onScroll", "hasScrollbars", "scrollDirection", "style"]);
    // TODO: replace RefForwardingComponent with ForwardRefRenderingFunction when
    // upgrading React.
    var ref = r;
    var _b = useScrollbars({
        containerRef: ref,
        containerWidth: containerWidth,
        containerHeight: containerHeight,
        contentWidth: contentWidth,
        contentHeight: contentHeight,
        snap: scrollBehaviour === ScrollBehaviour.Paging,
        allowedDirections: scrollDirection,
        containerLeft: style.left,
        containerTop: style.top,
        hasScrollbars: hasScrollbars,
    }), onContainerMouseDown = _b.onContainerMouseDown, onContainerScroll = _b.onContainerScroll, isDragging = _b.isDragging, rest = __rest(_b, ["onContainerMouseDown", "onContainerScroll", "isDragging"]);
    var onScroll = useCallback(function (e) {
        onContainerScroll();
        if (recordScroll) {
            recordScroll(e);
        }
    }, [onContainerScroll, recordScroll]);
    return (React.createElement(React.Fragment, null,
        React.createElement(ScrollbarContainer, __assign({ className: styles.scrollContainer, onMouseDown: onContainerMouseDown, onScroll: onScroll, ref: ref, style: __assign(__assign(__assign(__assign({}, style), { cursor: isDragging ? 'grabbing' : 'grab', userSelect: isDragging ? 'none' : 'none' }), getOverflowStyle(true, scrollDirection)), getScrollSnapType(scrollBehaviour, scrollDirection, isDragging)) }, props), children),
        React.createElement(Scrollbars, __assign({}, rest))));
});
// On mobile horizontal scrolling works natively and we don't need to customise
// the scrollbars. Therefore we can just use css to constrain scroll direction
// and show/hide scrollbars.
var MobileScrollContainer = forwardRef(function (_a, r) {
    var _b;
    var children = _a.children, isEnabled = _a.isEnabled, hasScrollbars = _a.hasScrollbars, scrollDirection = _a.scrollDirection, style = _a.style, scrollBehaviour = _a.scrollBehaviour, props = __rest(_a, ["children", "isEnabled", "hasScrollbars", "scrollDirection", "style", "scrollBehaviour"]);
    // TODO: replace RefForwardingComponent with ForwardRefRenderingFunction when
    // upgrading React.
    var ref = r;
    var containerClass = cx(styles.scrollContainer, (_b = {},
        _b[styles.hideScrollbars] = !hasScrollbars,
        _b));
    return (React.createElement("div", __assign({ className: containerClass, ref: ref, style: __assign(__assign(__assign({}, style), getOverflowStyle(isEnabled, scrollDirection)), getScrollSnapType(scrollBehaviour, scrollDirection)) }, props), children));
});
var ScrollContainer = function (_a) {
    var id = _a.id, src = _a.src, ownerScreenID = _a.ownerScreenID, imageHeight = _a.imageHeight, imageWidth = _a.imageWidth, hasScrollbars = _a.hasScrollbars, hotspots = _a.hotspots, left = _a.left, top = _a.top, width = _a.width, height = _a.height, scrollDirection = _a.scrollDirection, scrollBehaviour = _a.scrollBehaviour, 
    // When transitioning to another screen or overlay we need to know the scroll
    // position of the current screen or overlay for two reasons:
    //
    //   1. to honour maintainScroll and scroll the target to the same position as
    //      the current screen/overlay
    //   2. workaround the unfortunate fact that the way we manage screens with
    //      <CurrentScreen /><PreviousScreen / > means the current screen/overlay
    //      *re-renders* at that start of a transition and so we need to re-apply
    //      it's scroll position (looking at fixing this is long overdue -- either
    //      by managing two screens better and avoiding the re-render or even
    //      rendering all screens as the Backbone player did)
    //
    // It'd be nice to find a better solution than passing these props around. We
    // could use context so all descedents of screen/overlay can query the scroll position?
    getOverlayScrollPos = _a.getOverlayScrollPos, getScreenScrollPos = _a.getScreenScrollPos, isMobile = _a.isMobile, onScroll = _a.onScroll, scrollPosition = _a.scrollPosition, isEnabled = _a.isEnabled, isRecording = _a.isRecording, isPlaying = _a.isPlaying, fixed = _a.fixed;
    var scrollContainerRef = useRef(null);
    // Unpack to so the effect only runs when the values actually change
    // (considered using reselect to memoize the caculation of scrollPositions)
    var _b = __read(scrollPosition, 2), scrollLeft = _b[0], scrollTop = _b[1];
    useLayoutEffect(function () {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollLeft = scrollLeft;
        }
    }, [scrollLeft]);
    useLayoutEffect(function () {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollTop;
        }
    }, [scrollTop]);
    var scrollHandler = useCallback(function (e) {
        // TODO is it better to add a single scroll handler and inspect the
        // event to see which element scrolled rather than adding scroll
        // handlers to component that can scroll? We've gone that route
        // for miss clicks.
        e.stopPropagation(); // prevent bubbling up to the screen
        onScroll(e.target.scrollLeft, e.target.scrollTop, id);
    }, [id, onScroll]);
    var content = (React.createElement("div", { className: styles.hotspotContainer, style: { width: imageWidth } },
        React.createElement("img", { className: styles.content, "data-propagate-scroll": fixed && !isEnabled, draggable: false, src: src, style: {
                top: 0,
                left: 0,
                width: imageWidth,
                // Need maxWidth as some global style has img { max-width: 100% }.
                // Grrr
                maxWidth: imageWidth,
            } }),
        scrollBehaviour === ScrollBehaviour.Paging && !isPlaying && (React.createElement(ScrollSnapMarkers, { containerHeight: height, containerWidth: width, imageHeight: imageHeight, imageWidth: imageWidth })),
        hotspots.map(function (h) { return (React.createElement(ConnectedHotspot
        // TODO: remove using ForwardRefRenderingFunction
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        , { 
            // TODO: remove using ForwardRefRenderingFunction
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            getOverlayScrollPos: getOverlayScrollPos, getScreenScrollPos: getScreenScrollPos, id: h.id, location: "OVERLAY", screenID: ownerScreenID })); })));
    if (isMobile || !isEnabled) {
        return (React.createElement(MobileScrollContainer, { "data-layer-id": id, "data-layer-type": SCROLL_CONTAINER_LAYER, hasScrollbars: hasScrollbars, isEnabled: isEnabled, onScroll: isRecording ? scrollHandler : undefined, 
            // TODO: remove using ForwardRefRenderingFunction
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            ref: scrollContainerRef, scrollBehaviour: scrollBehaviour, scrollDirection: scrollDirection, style: {
                left: left,
                top: top,
                width: width,
                height: height,
            } }, content));
    }
    return (React.createElement(DesktopScrollContainer, { containerHeight: height, containerWidth: width, contentHeight: imageHeight, contentWidth: imageWidth, "data-layer-id": id, "data-layer-type": SCROLL_CONTAINER_LAYER, hasScrollbars: hasScrollbars, onScroll: isRecording ? scrollHandler : undefined, 
        // TODO: remove using ForwardRefRenderingFunction
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ref: scrollContainerRef, scrollBehaviour: scrollBehaviour, scrollDirection: scrollDirection, style: {
            left: left,
            top: top,
            width: width,
            height: height,
        } }, content));
};
function getOverflowStyle(isEnabled, scrollDirection) {
    if (!isEnabled) {
        return { overflow: 'hidden' };
    }
    if (scrollDirection === ScrollDirection.Both) {
        return { overflow: 'scroll' };
    }
    if (scrollDirection === ScrollDirection.Horizontal) {
        return { overflowX: 'scroll', overflowY: 'hidden' };
    }
    return { overflowX: 'hidden', overflowY: 'scroll' };
}
function mapStateToProps(state, _a) {
    var id = _a.id, ownerScreenID = _a.ownerScreenID, isCurrentScreen = _a.isCurrentScreen, destinationScreenID = _a.destinationScreenID;
    var screen = state.project.config.screenMap[destinationScreenID];
    // TODO this won't work for nested scroll containers (not sure if we're going
    // to officially support that yet or not)
    var hostScreen = getScreenById(state, ownerScreenID);
    var scale = isCurrentScreen
        ? getCurrentScale(state)
        : getScale(state, getPreviousScreen(state).img);
    return {
        src: screen.img.url,
        // We ignore fixed headers and footers on scroll containers so we just get
        // the body hotspots here
        hotspots: getBodyHotspots(state, screen, hostScreen),
        scaleFunc: scale,
        imageWidth: scale(screen.img.width),
        imageHeight: scale(screen.img.height),
        isMobile: state.deviceDetection.isMobile,
        // TODO do we need overscroll fix for iOS as we do for screen/overlay?
        // isIOS: state.deviceDetection.isIOS,
        isRecording: isRecordingSelector(state),
        isPlaying: isPlayingSelector(state),
        scrollPosition: getScrollPosition(state, id, scale),
    };
}
function mapDispatchToProps(dispatch) {
    return {
        // if ownProps is used this needs to be hoisted out of here as it will be recreated every time
        // a prop changes
        onScroll: throttle(function (scrollLeft, scrollTop, screenID) {
            return dispatch(scroll(SCROLL_CONTAINER_LAYER, scrollLeft, scrollTop, screenID));
        }, 50),
    };
}
export default connect(mapStateToProps, mapDispatchToProps)(ScrollContainer);
